import { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    LinearProgress,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import ReactFeather from 'feather-icons-react';
import Swal from 'sweetalert2';
import { uploadRequestedDocuments } from './api';

const MultiDocumentUploader = ({
    isOpen,
    setOpen,
    onClose,
    document,
    onDocumentUploaded,
    token,
    maxFiles = 5,
}) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        if (selectedFiles.length > maxFiles) {
            setError(`You can upload a maximum of ${maxFiles} files.`);
            return;
        }
        if (selectedFiles.length > 0) {
            setFiles(selectedFiles);
            setError(null);
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files);
        if (droppedFiles.length > maxFiles) {
            setError(`You can upload a maximum of ${maxFiles} files.`);
            return;
        }
        if (droppedFiles.length > 0) {
            setFiles(droppedFiles);
            setError(null);
        }
    };

    const resetUploader = () => {
        setFiles([]);
        setUploadProgress(0);
        setError(null);
        setOpen(true);
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setError("Please select at least one file to upload");
            return;
        }

        try {
            setUploading(true);
            setUploadProgress(0);

            const formData = new FormData();
            files.forEach((file) => {
                formData.append('documents', file);
            });

            const response = await uploadRequestedDocuments(formData, token, (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    setUploadProgress(progress);
                }
            });

            setUploadProgress(100);
            setUploading(false);
            onClose();

            Swal.fire({
                title: 'Files Uploaded',
                text: `Are you sure you want to keep the uploaded files for "${document.name}"?`,
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: 'Yes, keep them',
                cancelButtonText: 'No, discard'
            }).then((result) => {
                if (result.isConfirmed) {
                    const newUrls = response.data.map((item) => ({
                        file_url: item.url || item.fileUrl,
                        uploaded_by: item.tenantId || null,
                        uploaded_at: new Date(),
                    }));
                    onDocumentUploaded({
                        ...document,
                        urls: [...(document.urls || []), ...newUrls],
                    });
                } else {
                    resetUploader();
                }
            });
        } catch (err) {
            console.error("Error during upload:", err);
            setError("Failed to upload documents. Please try again.");
            setUploading(false);
        }
    };

    const handleCancel = () => {
        if (uploading) {
            Swal.fire({
                title: 'Cancel Upload?',
                text: 'Are you sure you want to cancel the upload?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, cancel upload',
                cancelButtonText: 'No, continue upload'
            }).then((result) => {
                if (result.isConfirmed) {
                    setUploading(false);
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={uploading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                    Upload Documents: {document?.name}
                </Typography>
                <IconButton onClick={handleCancel} size="small" disabled={uploading}>
                    <ReactFeather icon='x-circle' size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box
                    sx={{
                        border: '2px dashed #cccccc',
                        borderRadius: 2,
                        p: 3,
                        mb: 2,
                        textAlign: 'center',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        '&:hover': {
                            borderColor: '#999999'
                        }
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                        multiple
                        disabled={uploading}
                    />
                    <ReactFeather icon="upload-cloud" size={40} color="#666666" />
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        {files.length > 0
                            ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
                            : 'Drag & drop files here or click to browse'}
                    </Typography>
                    {files.length > 0 && (
                        <List dense sx={{ mt: 1, maxHeight: '100px', overflow: 'auto' }}>
                            {files.map((file, index) => (
                                <ListItem key={index}>
                                    <ListItemText
                                        primary={file.name}
                                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Maximum {maxFiles} files allowed
                    </Typography>
                </Box>

                {uploading && (
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Uploading...</Typography>
                            <Typography variant="body2">{uploadProgress}%</Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={handleCancel}
                    disabled={uploading && uploadProgress > 0 && uploadProgress < 100}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    color="primary"
                    disabled={files.length === 0 || uploading}
                    startIcon={<ReactFeather icon="upload" size={16} />}
                >
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { MultiDocumentUploader };